/* Step 3: Review and send step for document stepper
   Created: Shows full document preview with totals, and send/download actions
   Updated: BTW breakdown now groups per rate (0%/9%/21%) based on each line's
   btw_percentage instead of assuming 9%. src="/assets/logos/buitnplanten-logo-green.webp" don't change */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { COMPANY_INFO } from '@/lib/company-info';
import { calculateBTWBreakdown } from '@/lib/btw-calculator';
import { User, Mail, Phone, MapPin, FileText, CalendarDays } from 'lucide-react';
import type { LineItem, CustomerData, DocumentType } from './types';

interface ReviewStepProps {
  items: LineItem[];
  customer: CustomerData;
  documentType: DocumentType;
  documentNumber: string;
  deliveryFee: number;
  discountAmount: number;
}

export default function ReviewStep({
  items,
  customer,
  documentType,
  documentNumber,
  deliveryFee,
  discountAmount,
}: ReviewStepProps) {
  const isInvoice = documentType === 'invoice';
  const subtotal = items.reduce((sum, i) => sum + i.total_price, 0);
  const total = subtotal + deliveryFee - discountAmount;
  const btwBreakdown = calculateBTWBreakdown(
    items.map((i) => ({ totalInclBTW: i.total_price, btwPercentage: i.btw_percentage }))
  );

  const today = new Date().toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className="max-w-3xl mx-auto">
      <Card className="shadow-lg border-0 overflow-hidden">
        <div className="h-1.5 bg-jv-primary" />
        <CardContent className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <img
                src="/assets/logos/buitnplanten-logo-green.webp"
                alt="Buitn Planten"
                className="h-10 mb-3"
              />
              <div className="text-xs text-gray-500 space-y-0.5">
                <p>{COMPANY_INFO.name}</p>
                <p>{COMPANY_INFO.address}</p>
                <p>{COMPANY_INFO.postalCode} {COMPANY_INFO.city}</p>
                <p className="mt-1">KvK: {COMPANY_INFO.kvk}</p>
                <p>BTW: {COMPANY_INFO.btw}</p>
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-jv-primary mb-2">
                {isInvoice ? 'FACTUUR' : 'OFFERTE'}
              </h2>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p className="font-semibold">{documentNumber}</p>
                <p>Datum: {today}</p>
                {!isInvoice && customer.valid_until && (
                  <p>
                    Geldig tot:{' '}
                    {new Date(customer.valid_until).toLocaleDateString('nl-NL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase">Klantgegevens</span>
            </div>
            <p className="text-sm font-semibold text-gray-900">{customer.name}</p>
            {[customer.street, customer.housenumber, customer.housenumber_addition].filter(Boolean).join(' ') && <p className="text-xs text-gray-600">{[customer.street, customer.housenumber, customer.housenumber_addition].filter(Boolean).join(' ')}</p>}
            {(customer.postal_code || customer.city) && (
              <p className="text-xs text-gray-600">{customer.postal_code} {customer.city}</p>
            )}
            <div className="flex items-center gap-4 mt-1.5">
              {customer.email && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {customer.email}
                </span>
              )}
              {customer.phone && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {customer.phone}
                </span>
              )}
            </div>
          </div>

          {customer.notes && (
            <div className="bg-[#e7e1d6]/40 border-l-4 border-[#1e2b22] rounded-r-lg p-3 mb-6">
              <p className="text-xs font-semibold text-jv-primary mb-1">Opmerkingen</p>
              <p className="text-xs text-gray-600">{customer.notes}</p>
            </div>
          )}

          <table className="w-full mb-6">
            <thead>
              <tr className="bg-jv-primary text-white">
                <th className="text-left text-xs font-semibold px-3 py-2.5 rounded-tl-lg">Omschrijving</th>
                <th className="text-center text-xs font-semibold px-3 py-2.5 w-20">Aantal</th>
                <th className="text-right text-xs font-semibold px-3 py-2.5 w-28">Prijs/stuk (incl. BTW)</th>
                <th className="text-right text-xs font-semibold px-3 py-2.5 w-28 rounded-tr-lg">Totaal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id} className={index % 2 === 0 ? 'bg-gray-50/50' : ''}>
                  <td className="px-3 py-2.5 text-sm text-gray-900">
                    {item.description}
                    {item.is_custom && (
                      <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1 bg-[#f5eede] text-[#7a6450] border-[#e7d9c0]">
                        Vrij
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-center text-gray-600">{item.quantity}</td>
                  <td className="px-3 py-2.5 text-sm text-right text-gray-600">
                    &euro;{item.unit_price.toFixed(2)}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-right font-medium text-gray-900">
                    &euro;{item.total_price.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mb-8">
            <div className="w-72 bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Subtotaal excl. BTW:</span>
                <span>&euro;{btwBreakdown.totalExclBTW.toFixed(2)}</span>
              </div>
              {btwBreakdown.entries.map((entry) => (
                <div key={entry.percentage} className="flex justify-between text-xs text-gray-600">
                  <span>BTW ({entry.percentage}%):</span>
                  <span>&euro;{entry.btwAmount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs text-gray-600">
                <span>Subtotaal incl. BTW:</span>
                <span>&euro;{subtotal.toFixed(2)}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Bezorgkosten:</span>
                  <span>&euro;{deliveryFee.toFixed(2)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between text-xs text-[#1e2b22]">
                  <span>Korting:</span>
                  <span>- &euro;{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t-2 border-jv-primary pt-2 mt-2">
                <div className="flex justify-between font-bold text-sm">
                  <span>Totaal incl. BTW:</span>
                  <span className="text-jv-primary">&euro;{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {isInvoice ? (
            <div className="bg-[#f5eede] border-2 border-[#c8a94a]/50 rounded-lg p-4 mb-6">
              <p className="text-xs font-semibold text-jv-primary mb-2">Betaalinformatie</p>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>Gelieve het totaalbedrag over te maken naar:</p>
                <p className="mt-1"><strong>Rekeningnummer:</strong> {COMPANY_INFO.bankAccount}</p>
                <p><strong>Ten name van:</strong> {COMPANY_INFO.bankAccountName}</p>
                <p><strong>Bank:</strong> {COMPANY_INFO.bankName}</p>
                <p className="mt-1"><strong>Onder vermelding van:</strong> {documentNumber}</p>
              </div>
            </div>
          ) : (
            <div className="bg-[#e7e1d6]/40 border-2 border-[#1e2b22]/30 rounded-lg p-4 mb-6">
              <p className="text-xs font-semibold text-jv-primary mb-2">Offerte informatie</p>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>Deze offerte is vrijblijvend en onder voorbehoud van beschikbaarheid.</p>
                {customer.valid_until && (
                  <p className="mt-1">
                    <strong>Geldig tot:</strong>{' '}
                    {new Date(customer.valid_until).toLocaleDateString('nl-NL', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                )}
                <p className="mt-1">Neem contact met ons op om deze offerte om te zetten naar een bestelling.</p>
              </div>
            </div>
          )}

          <div className="text-center text-xs text-gray-400 pt-4 border-t">
            <p>{isInvoice ? 'Bedankt voor de bestelling!' : 'Bedankt voor uw interesse in Buitn Planten!'}</p>
            <p className="mt-0.5">{COMPANY_INFO.name} | {COMPANY_INFO.email} | {COMPANY_INFO.website}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
